'use client';

import type { SearchResponse } from '@ria-client/advisor-search/types';

import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { LoadingSkeleton } from './LoadingSkeleton';
import { ResultsHeader } from './ResultsHeader';
import { ResultsList } from './ResultsList';
import { ResultsPagination } from './ResultsPagination';

export type ResultsContentProps = {
  data: SearchResponse | null;
  isPending: boolean;
  error: string | null;
  retry: () => void;
  zip: string;
  activeFilterCount: number;
  currentPage: number;
  startPosition: number;
  onPageChange: (page: number) => void;
};

export function ResultsContent({
  data,
  isPending,
  error,
  retry,
  zip,
  activeFilterCount,
  currentPage,
  startPosition,
  onPageChange,
}: ResultsContentProps) {
  if (isPending && !data) {
    return <LoadingSkeleton />;
  }

  if (error && !isPending) {
    return <ErrorState onRetry={retry} />;
  }

  if (!data) {
    return null;
  }

  if (data.advisors.length === 0) {
    return (
      <EmptyState variant={activeFilterCount > 0 ? 'filters-too-narrow' : 'no-area-results'} />
    );
  }

  return (
    <div
      aria-busy={isPending}
      className={`transition-opacity duration-150 ease-[ease] ${
        isPending ? 'pointer-events-none opacity-60' : 'pointer-events-auto opacity-100'
      }`}
    >
      <ResultsHeader total={data.total} zip={zip} activeFilterCount={activeFilterCount} />
      <ResultsList advisors={data.advisors} startPosition={startPosition} />
      <ResultsPagination
        page={currentPage}
        totalPages={data.totalPages}
        onPageChange={onPageChange}
        isLoading={isPending}
      />
    </div>
  );
}
